// Phase 2 Wave 5 Agent S1 - MFA challenge modal (login flow).
//
// Renders after /auth/login returns ``mfa_required: true``. Accepts the
// 6-digit code (or a recovery code) and trades the challenge token for
// a real access + refresh token via /mfa/verify.
import { useState } from 'react'
import { Loader2, ShieldCheck } from 'lucide-react'
import { mfaApi } from '@/api/mfa'

export interface MFAChallengeProps {
  challengeToken: string
  method: 'totp' | 'sms'
  onSuccess: (token: {
    access_token: string
    refresh_token?: string | null
    user: any
  }) => void
  onCancel?: () => void
}

export default function MFAChallengeModal({
  challengeToken,
  method,
  onSuccess,
  onCancel,
}: MFAChallengeProps) {
  const [code, setCode] = useState('')
  const [isRecovery, setIsRecovery] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const token = await mfaApi.verify({
        challenge_token: challengeToken,
        code: code.trim(),
        is_recovery_code: isRecovery,
      })
      onSuccess(token)
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Verification failed')
    } finally {
      setBusy(false)
    }
  }

  const friendlyMethod =
    method === 'sms' ? 'SMS code' : 'authenticator code'
  const placeholder = isRecovery ? 'XXXXX-XXXXX' : '000000'

  return (
    <div
      data-testid="mfa-challenge-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-bold">Two-factor authentication</h2>
        </div>
        <p className="mb-4 text-sm text-gray-600">
          {isRecovery
            ? 'Enter one of your single-use recovery codes.'
            : `Enter the 6-digit ${friendlyMethod} to finish signing in.`}
        </p>
        <form onSubmit={submit}>
          <input
            type="text"
            value={code}
            inputMode={isRecovery ? 'text' : 'numeric'}
            onChange={(e) =>
              setCode(
                isRecovery
                  ? e.target.value.toUpperCase()
                  : e.target.value.replace(/\D/g, ''),
              )
            }
            maxLength={isRecovery ? 11 : 6}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="mfa-challenge-code-input"
            placeholder={placeholder}
            autoFocus
          />
          {error && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy || !code}
            className="mt-4 w-full rounded-lg bg-blue-600 py-2 font-semibold text-white shadow disabled:opacity-50"
            data-testid="mfa-challenge-verify-button"
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify'
            )}
          </button>
        </form>
        <div className="mt-4 flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={() => setIsRecovery(!isRecovery)}
            className="text-blue-600 hover:underline"
            data-testid="mfa-challenge-toggle-recovery"
          >
            {isRecovery
              ? 'Use authenticator code instead'
              : 'Use a recovery code'}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="text-gray-500 hover:underline"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
